// src/pages/Settings.js

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Download,
  Eye,
  EyeOff,
  Copy,
  Check,
  ArrowRight,
  Shield,
  Lock,
  ShieldCheck,
  User,
  AlertTriangle,
  ArrowLeft,
} from "lucide-react";

import "../components/Walletview.css";


// =====================================================
// SETTINGS PAGE
// =====================================================

const Settings = () => {

  // =====================================================
  // WALLET INFORMATION
  // =====================================================

  const [seedPhrase, setSeedPhrase] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [address, setAddress] = useState("");

  const [message, setMessage] = useState("");

  const navigate = useNavigate();


  // =====================================================
  // VISIBILITY STATES
  // =====================================================

  const [showSeed, setShowSeed] = useState(false);
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [showAddress, setShowAddress] = useState(false);


  // =====================================================
  // COPY STATES
  // =====================================================

  const [copiedSeed, setCopiedSeed] = useState(false);
  const [copiedPrivateKey, setCopiedPrivateKey] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState(false);


  // =====================================================
  // BACK TO WALLET
  // =====================================================

  const handleBackToWallet = () => {
    navigate("/yourwallet");
  };


  // =====================================================
  // RETRIEVE EXISTING WALLET
  // =====================================================

  const handleRetrieveWallet = () => {

    const storedSeedPhrase =
      localStorage.getItem("seedPhrase");

    const storedPrivateKey =
      localStorage.getItem("privateKey");

    const storedAddress =
      localStorage.getItem("address");


    if (
      storedSeedPhrase &&
      storedPrivateKey &&
      storedAddress
    ) {

      setSeedPhrase(storedSeedPhrase);

      setPrivateKey(storedPrivateKey);

      setAddress(storedAddress);

      setMessage(
        "Wallet retrieved successfully!"
      );

      setShowSeed(false);

      setShowPrivateKey(false);

      setShowAddress(false);

    } else {

      setMessage(
        "No existing wallet found on this device."
      );

    }
  };


  // =====================================================
  // COPY SEED PHRASE
  // =====================================================

  const handleCopySeed = async () => {

    if (!seedPhrase) return;

    try {

      await navigator.clipboard.writeText(
        seedPhrase
      );

      setCopiedSeed(true);

      setTimeout(() => {
        setCopiedSeed(false);
      }, 2000);

    } catch (error) {

      console.error(
        "Failed to copy seed phrase:",
        error
      );

    }
  };


  // =====================================================
  // COPY PRIVATE KEY
  // =====================================================

  const handleCopyPrivateKey = async () => {

    if (!privateKey) return;

    try {

      await navigator.clipboard.writeText(
        privateKey
      );

      setCopiedPrivateKey(true);

      setTimeout(() => {
        setCopiedPrivateKey(false);
      }, 2000);

    } catch (error) {

      console.error(
        "Failed to copy private key:",
        error
      );

    }
  };


  // =====================================================
  // COPY ADDRESS
  // =====================================================

  const handleCopyAddress = async () => {

    if (!address) return;

    try {

      await navigator.clipboard.writeText(
        address
      );

      setCopiedAddress(true);

      setTimeout(() => {
        setCopiedAddress(false);
      }, 2000);

    } catch (error) {

      console.error(
        "Failed to copy address:",
        error
      );

    }
  };


  // =====================================================
  // EXPORT WALLET BACKUP
  // =====================================================

  const handleExportBackup = () => {

    const storedSeedPhrase =
      localStorage.getItem("seedPhrase");

    const storedPrivateKey =
      localStorage.getItem("privateKey");

    const storedAddress =
      localStorage.getItem("address");


    if (
      !storedSeedPhrase ||
      !storedPrivateKey ||
      !storedAddress
    ) {

      setMessage(
        "No wallet data found. Please retrieve your wallet first."
      );

      return;
    }


    try {

      const backupData = {

        seedPhrase: storedSeedPhrase,

        privateKey: storedPrivateKey,

        address: storedAddress,

        exportedAt:
          new Date().toISOString(),

      };


      const jsonData =
        JSON.stringify(
          backupData,
          null,
          2
        );


      const blob =
        new Blob(
          [jsonData],
          {
            type: "application/json",
          }
        );


      const url =
        window.URL.createObjectURL(blob);


      const link =
        document.createElement("a");


      link.href = url;


      const timestamp =
        new Date()
          .toISOString()
          .replace(/[:.]/g, "-");


      link.download =
        `bait-wallet-backup-${timestamp}.json`;


      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);


      window.URL.revokeObjectURL(url);


      setMessage(
        "Wallet backup exported successfully! Keep it safe."
      );

    } catch (error) {

      console.error(
        "Export error:",
        error
      );

      setMessage(
        "Failed to export wallet backup."
      );
    }
  };


  // =====================================================
  // FORMAT ADDRESS
  // =====================================================

  const formatAddress = (addr) => {

    if (!addr) {
      return "No address available";
    }

    if (addr.length <= 16) {
      return addr;
    }

    return `${addr.slice(0, 8)}......${addr.slice(-6)}`;
  };


  // =====================================================
  // SETTINGS STYLES
  // =====================================================

  const styles = {

    // ===================================================
    // PAGE
    // ===================================================

    page: {
      width: "100%",
      minHeight: "87vh",

      boxSizing: "border-box",

      display: "flex",
      flexDirection: "column",

      overflowX: "hidden",
    },


    // ===================================================
    // MAIN SETTINGS CONTAINER
    // ===================================================

    container: {
      width: "calc(100% - 32px)",

      maxWidth: 640,

      margin: "8px auto 28px",

      padding: "0 0 24px",

      boxSizing: "border-box",
    },


    // ===================================================
    // PAGE TITLE
    // ===================================================

    pageTitle: {
      width: "100%",

      marginBottom: 18,

      boxSizing: "border-box",
    },


    titleWrapper: {
      position: "relative",

      width: "100%",

      minHeight: 64,

      display: "flex",

      alignItems: "center",

      justifyContent: "center",

      boxSizing: "border-box",
    },


    backButton: {
      position: "absolute",

      left: 0,

      top: "50%",

      transform: "translateY(-50%)",

      width: 40,
      height: 40,

      padding: 0,

      display: "flex",

      alignItems: "center",

      justifyContent: "center",

      background:
        "rgba(154,106,24,0.08)",

      border:
        "1px solid rgba(154,106,24,0.18)",

      borderRadius: 10,

      color: "#9a6a18",

      cursor: "pointer",

      transition:
        "all 0.2s ease",

      boxSizing: "border-box",

      flexShrink: 0,
    },


    titleContent: {
      width: "100%",

      textAlign: "center",

      padding: "0 52px",

      boxSizing: "border-box",
    },


    // ===================================================
    // TITLE — matches BAIT PAY brand styling
    // ===================================================

    title: {
      margin: "0 0 5px",

      fontFamily:
        "'Inter', 'Poppins', 'Segoe UI', sans-serif",

      fontSize: 26,

      lineHeight: 1.1,

      fontWeight: 600,

      letterSpacing: "1.4px",

      color: "#8a5a1f",

      WebkitTextStroke:
        "0.4px #6b3d0a",

      textShadow:
        "0 1px 0 rgba(255, 240, 200, 0.6), 0 2px 6px rgba(154, 106, 24, 0.22)",
    },


    // ===================================================
    // SUBTITLE — muted bronze
    // ===================================================

    subtitle: {
      margin: "4px 0 0",

      fontFamily:
        "'Poppins', 'Segoe UI', sans-serif",

      fontSize: 11,

      fontWeight: 500,

      letterSpacing: "0.6px",

      lineHeight: 1.3,

      color: "#a07a3f",

      opacity: 0.95,
    },


    // ===================================================
    // WALLET MANAGEMENT
    // ===================================================

    management: {
      width: "100%",

      marginBottom: 16,

      boxSizing: "border-box",
    },


    sectionTitle: {
      margin: "0 0 8px",

      fontSize: 13,

      lineHeight: 1.25,

      fontWeight: 700,

      color: "#6f4d13",

      letterSpacing: "0.2px",
    },


    // ===================================================
    // ACTIONS
    // ===================================================

    actions: {
      width: "100%",

      display: "flex",

      alignItems: "stretch",

      margin: 0,

      padding: 0,

      boxSizing: "border-box",
    },


    // ===================================================
    // RETRIEVE CARD
    // ===================================================

    retrieveCard: {
      width: "100%",

      minHeight: 70,

      padding: "10px 14px",

      display: "grid",

      gridTemplateColumns: "36px minmax(0, 1fr)",

      gridTemplateRows: "auto auto",

      columnGap: 11,

      alignItems: "center",

      boxSizing: "border-box",

      background:
        "linear-gradient(135deg, #fbf7ef 0%, #f8f0e1 100%)",

      border:
        "1px solid rgba(154,106,24,0.22)",

      borderRadius: 11,

      color: "#2f2618",

      cursor: "pointer",

      transition:
        "all 0.2s ease",

      boxShadow:
        "0 2px 8px rgba(154,106,24,0.035)",
    },


    retrieveIcon: {
      width: 34,
      height: 34,

      gridRow: "1 / span 2",

      display: "flex",

      alignItems: "center",

      justifyContent: "center",

      margin: 0,

      borderRadius: 9,

      background:
        "rgba(154,106,24,0.10)",

      border:
        "1px solid rgba(154,106,24,0.12)",

      color: "#9a6a18",

      boxSizing: "border-box",
    },


    retrieveTitle: {
      margin: 0,

      fontSize: 12,

      lineHeight: 1.2,

      fontWeight: 700,

      color: "#4f3a17",
    },


    retrieveText: {
      margin: "2px 0 0",

      fontSize: 9.5,

      lineHeight: 1.35,

      color: "#9b8b73",
    },


    // ===================================================
    // WALLET INFORMATION
    // ===================================================

    infoSection: {
      width: "100%",

      marginTop: 0,

      padding: "15px",

      boxSizing: "border-box",

      background:
        "linear-gradient(135deg, #fbf7ef 0%, #faf4e9 100%)",

      border:
        "1px solid rgba(154,106,24,0.24)",

      borderRadius: 13,

      boxShadow:
        "0 3px 12px rgba(154,106,24,0.045)",
    },


    infoSectionTitle: {
      margin: "0 0 10px",

      fontSize: 13,

      fontWeight: 700,

      color: "#6f4d13",

      letterSpacing: "0.15px",
    },


    // ===================================================
    // INFO ITEM
    // ===================================================

    infoItem: {
      width: "100%",

      display: "grid",

      gridTemplateColumns:
        "100px minmax(0, 1fr) auto",

      alignItems: "center",

      gap: 9,

      padding: "8px 9px",

      marginBottom: 7,

      boxSizing: "border-box",

      background: "#f7efdf",

      border:
        "1px solid rgba(154,106,24,0.10)",

      borderRadius: 9,

      minWidth: 0,

      transition:
        "border-color 0.2s ease",
    },


    infoItemLast: {
      marginBottom: 0,
    },


    infoLabel: {
      minWidth: 0,

      display: "flex",

      alignItems: "center",
    },


    infoLabelText: {
      fontSize: 9.5,

      fontWeight: 700,

      color: "#80652f",

      whiteSpace: "nowrap",

      overflow: "hidden",

      textOverflow: "ellipsis",
    },


    infoValue: {
      height: 34,

      minWidth: 0,

      display: "flex",

      alignItems: "center",

      padding: "0 10px",

      boxSizing: "border-box",

      background: "#fbf7ef",

      border:
        "1px solid rgba(154,106,24,0.12)",

      borderRadius: 7,

      color: "#4f3a17",

      fontSize: 10.5,

      fontFamily:
        "Consolas, 'Courier New', monospace",

      overflow: "hidden",

      whiteSpace: "nowrap",

      textOverflow: "ellipsis",
    },


    // ===================================================
    // ACTION BUTTONS
    // ===================================================

    addressActions: {
      display: "flex",

      alignItems: "center",

      gap: 5,

      flexShrink: 0,
    },


    smallIconButton: {
      width: 31,
      height: 31,

      padding: 0,

      display: "flex",

      alignItems: "center",

      justifyContent: "center",

      background:
        "rgba(154,106,24,0.08)",

      border:
        "1px solid rgba(154,106,24,0.12)",

      borderRadius: 7,

      color: "#80652f",

      cursor: "pointer",

      transition:
        "all 0.2s ease",

      boxSizing: "border-box",
    },


    // ===================================================
    // EXPORT SECTION
    // ===================================================

    exportSection: {
      width: "100%",

      marginTop: 16,

      padding: "15px",

      boxSizing: "border-box",

      background:
        "linear-gradient(135deg, #fbf7ef 0%, #faf4e9 100%)",

      border:
        "1px solid rgba(154,106,24,0.24)",

      borderRadius: 13,

      boxShadow:
        "0 3px 12px rgba(154,106,24,0.045)",
    },


    exportHeader: {
      display: "flex",

      alignItems: "center",

      gap: 10,

      marginBottom: 10,
    },


    exportIcon: {
      width: 36,
      height: 36,

      minWidth: 36,

      display: "flex",

      alignItems: "center",

      justifyContent: "center",

      borderRadius: 9,

      background:
        "rgba(154,106,24,0.10)",

      border:
        "1px solid rgba(154,106,24,0.14)",

      color: "#9a6a18",

      boxSizing: "border-box",
    },


    exportTitle: {
      margin: "0 0 3px",

      fontSize: 13,

      fontWeight: 700,

      color: "#6f4d13",
    },


    exportDescription: {
      margin: 0,

      fontSize: 9.5,

      color: "#9b8b73",

      lineHeight: 1.4,
    },


    // ===================================================
    // FEATURE TAGS
    // ===================================================

    exportFeatures: {
      display: "flex",

      alignItems: "center",

      gap: 6,

      flexWrap: "wrap",

      marginBottom: 10,
    },


    featureTag: {
      display: "inline-flex",

      alignItems: "center",

      justifyContent: "center",

      gap: 4,

      padding: "5px 9px",

      borderRadius: 20,

      background: "#f4ead8",

      border:
        "1px solid rgba(154,106,24,0.12)",

      color: "#80652f",

      fontSize: 9,

      fontWeight: 600,

      boxSizing: "border-box",
    },


    // ===================================================
    // EXPORT BUTTON
    // ===================================================

    exportActions: {
      width: "100%",

      marginBottom: 10,
    },


    exportButton: {
      width: "100%",

      height: 40,

      display: "flex",

      alignItems: "center",

      justifyContent: "center",

      gap: 7,

      padding: "0 13px",

      background:
        "linear-gradient(135deg, #9a6a18 0%, #a9761f 100%)",

      border:
        "1px solid #9a6a18",

      borderRadius: 8,

      color: "#ffffff",

      fontSize: 11,

      fontWeight: 700,

      fontFamily:
        "'Inter', sans-serif",

      cursor: "pointer",

      transition:
        "all 0.2s ease",

      boxSizing: "border-box",

      boxShadow:
        "0 3px 8px rgba(154,106,24,0.12)",
    },


    // ===================================================
    // WARNING
    // ===================================================

    warning: {
      width: "100%",

      display: "flex",

      alignItems: "center",

      gap: 9,

      padding: "9px 10px",

      boxSizing: "border-box",

      background:
        "rgba(181,122,18,0.06)",

      border:
        "1px solid rgba(181,122,18,0.13)",

      borderRadius: 8,
    },


    warningIcon: {
      width: 27,
      height: 27,

      minWidth: 27,

      display: "flex",

      alignItems: "center",

      justifyContent: "center",

      borderRadius: 7,

      background:
        "rgba(181,122,18,0.10)",

      color: "#b17a12",
    },


    warningContent: {
      minWidth: 0,

      display: "flex",

      flexDirection: "column",

      gap: 1,
    },


    warningTitle: {
      fontSize: 9,

      fontWeight: 700,

      color: "#8a6112",
    },


    warningText: {
      fontSize: 8.5,

      lineHeight: 1.35,

      color: "#9b8b73",
    },


    // ===================================================
    // MESSAGE
    // ===================================================

    message: {
      width: "100%",

      marginTop: 10,

      padding: "9px 11px",

      boxSizing: "border-box",

      background:
        "rgba(154,106,24,0.08)",

      border:
        "1px solid rgba(154,106,24,0.18)",

      borderRadius: 8,

      color: "#80652f",

      fontSize: 10,

      lineHeight: 1.4,

      textAlign: "center",
    },
  };


  // =====================================================
  // RETURN
  // =====================================================

  return (

    <div
      className="settings-page"
      style={styles.page}
    >

      <div
        className="settings-container"
        style={styles.container}
      >

        {/* =================================================
            TITLE
        ================================================= */}

        <div
          className="settings-page-title"
          style={styles.pageTitle}
        >

          <div
            className="settings-title-wrapper"
            style={styles.titleWrapper}
          >

            {/* BACK BUTTON */}
            <button
              type="button"
              className="market-back-btn"
              onClick={() => navigate("/yourwallet")}
              title="Back to Wallet"
            >
              <ArrowLeft size={18} />
            </button>


            <div
              className="settings-title-content"
              style={styles.titleContent}
            >

              <h1 style={styles.title}>
                SETTING'S
              </h1>

              <p style={styles.subtitle}>
                Manage your wallet information
                and export your backup.
              </p>

            </div>

          </div>

        </div>


        {/* =================================================
            WALLET RETRIEVE
        ================================================= */}

        <div
          className="wallet-management"
          style={styles.management}
        >

          <h3
            className="section-title"
            style={styles.sectionTitle}
          >
            Wallet Management
          </h3>


          <div
            className="actions"
            style={styles.actions}
          >

            <div
              className="action-card retrieve-card"
              onClick={handleRetrieveWallet}
              style={styles.retrieveCard}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (
                  event.key === "Enter" ||
                  event.key === " "
                ) {
                  handleRetrieveWallet();
                }
              }}
            >

              <div
                className="action-icon"
                style={styles.retrieveIcon}
              >

                <Download size={18} />

              </div>


              <h4 style={styles.retrieveTitle}>
                Retrieve Wallet
              </h4>


              <p style={styles.retrieveText}>
                Load your existing wallet
                information from this device.
              </p>

            </div>

          </div>

        </div>


        {/* =================================================
            WALLET INFORMATION
        ================================================= */}

        <div
          className="wallet-info-section"
          style={styles.infoSection}
        >

          <h3
            className="section-title"
            style={styles.infoSectionTitle}
          >
            Wallet Information
          </h3>


          {/* SEED PHRASE */}

          <div
            className="info-item"
            style={styles.infoItem}
          >

            <div
              className="info-label"
              style={styles.infoLabel}
            >

              <span style={styles.infoLabelText}>
                Seed Phrase
              </span>

            </div>


            <div
              className="info-value-text"
              style={styles.infoValue}
            >

              {showSeed

                ? (
                    seedPhrase ||
                    "No seed phrase available"
                  )

                : (
                    "•••• •••• •••• •••• ••••"
                  )

              }

            </div>


            <div
              className="address-actions"
              style={styles.addressActions}
            >

              <button
                className="info-icon-btn"
                style={styles.smallIconButton}
                onClick={() => {

                  if (!seedPhrase) {
                    handleRetrieveWallet();
                  }

                  setShowSeed(!showSeed);

                }}
                aria-label={
                  showSeed
                    ? "Hide seed phrase"
                    : "Show seed phrase"
                }
              >

                {showSeed
                  ? <Eye size={15} />
                  : <EyeOff size={15} />
                }

              </button>


              <button
                className="copy-btn"
                style={styles.smallIconButton}
                onClick={handleCopySeed}
                aria-label="Copy seed phrase"
              >

                {copiedSeed
                  ? <Check size={15} />
                  : <Copy size={15} />
                }

              </button>

            </div>

          </div>


          {/* PRIVATE KEY */}

          <div
            className="info-item"
            style={styles.infoItem}
          >

            <div
              className="info-label"
              style={styles.infoLabel}
            >

              <span style={styles.infoLabelText}>
                Private Key
              </span>

            </div>


            <div
              className="info-value-text"
              style={styles.infoValue}
            >

              {showPrivateKey

                ? (
                    privateKey ||
                    "No private key available"
                  )

                : (
                    "••••••••••••••••••••••••••••"
                  )

              }

            </div>


            <div
              className="address-actions"
              style={styles.addressActions}
            >

              <button
                className="info-icon-btn"
                style={styles.smallIconButton}
                onClick={() => {

                  if (!privateKey) {
                    handleRetrieveWallet();
                  }

                  setShowPrivateKey(
                    !showPrivateKey
                  );

                }}
                aria-label={
                  showPrivateKey
                    ? "Hide private key"
                    : "Show private key"
                }
              >

                {showPrivateKey
                  ? <Eye size={15} />
                  : <EyeOff size={15} />
                }

              </button>


              <button
                className="copy-btn"
                style={styles.smallIconButton}
                onClick={handleCopyPrivateKey}
                aria-label="Copy private key"
              >

                {copiedPrivateKey
                  ? <Check size={15} />
                  : <Copy size={15} />
                }

              </button>

            </div>

          </div>


          {/* WALLET ADDRESS */}

          <div
            className="info-item"
            style={{
              ...styles.infoItem,
              ...styles.infoItemLast,
            }}
          >

            <div
              className="info-label"
              style={styles.infoLabel}
            >

              <span style={styles.infoLabelText}>
                Wallet Address
              </span>

            </div>


            <div
              className="info-value-text"
              style={styles.infoValue}
            >

              {showAddress

                ? (
                    address ||
                    "No address available"
                  )

                : (
                    address
                      ? formatAddress(address)
                      : "No address available"
                  )

              }

            </div>


            <div
              className="address-actions"
              style={styles.addressActions}
            >

              <button
                className="info-icon-btn"
                style={styles.smallIconButton}
                onClick={() => {

                  if (!address) {
                    handleRetrieveWallet();
                  }

                  setShowAddress(
                    !showAddress
                  );

                }}
                aria-label={
                  showAddress
                    ? "Hide wallet address"
                    : "Show wallet address"
                }
              >

                {showAddress
                  ? <Eye size={15} />
                  : <EyeOff size={15} />
                }

              </button>


              <button
                className="copy-btn"
                style={styles.smallIconButton}
                onClick={handleCopyAddress}
                aria-label="Copy wallet address"
              >

                {copiedAddress
                  ? <Check size={15} />
                  : <Copy size={15} />
                }

              </button>

            </div>

          </div>

        </div>


        {/* =================================================
            EXPORT BACKUP
        ================================================= */}

        <div
          className="export-section"
          style={styles.exportSection}
        >

          <div
            className="export-header"
            style={styles.exportHeader}
          >

            <div
              className="export-header-icon"
              style={styles.exportIcon}
            >

              <Shield size={20} />

            </div>


            <div>

              <h3
                className="section-title"
                style={styles.exportTitle}
              >
                Export Wallet Backup
              </h3>


              <p
                className="export-description"
                style={styles.exportDescription}
              >
                Download your wallet information
                and keep it safe.
              </p>

            </div>

          </div>


          {/* FEATURES */}

          <div
            className="export-features"
            style={styles.exportFeatures}
          >

            <div
              className="feature-tag"
              style={styles.featureTag}
            >

              <Lock size={13} />

              <span>
                Secure
              </span>

            </div>


            <div
              className="feature-tag"
              style={styles.featureTag}
            >

              <ShieldCheck size={13} />

              <span>
                Protected
              </span>

            </div>


            <div
              className="feature-tag"
              style={styles.featureTag}
            >

              <User size={13} />

              <span>
                Private
              </span>

            </div>

          </div>


          {/* EXPORT BUTTON */}

          <div
            className="export-actions"
            style={styles.exportActions}
          >

            <button
              className="export-btn"
              style={styles.exportButton}
              onClick={handleExportBackup}
            >

              <Download size={17} />

              <span>
                Export Backup
              </span>

              <ArrowRight
                size={16}
                className="btn-arrow"
              />

            </button>

          </div>


          {/* SECURITY WARNING */}

          <div
            className="export-warning"
            style={styles.warning}
          >

            <div
              className="warning-icon-wrapper"
              style={styles.warningIcon}
            >

              <AlertTriangle size={15} />

            </div>


            <div
              className="warning-content"
              style={styles.warningContent}
            >

              <span
                className="warning-title"
                style={styles.warningTitle}
              >
                Security Warning
              </span>


              <span
                className="warning-text"
                style={styles.warningText}
              >
                Never share your seed phrase,
                private key, or backup file
                with anyone.
              </span>

            </div>

          </div>

        </div>


        {/* =================================================
            MESSAGE
        ================================================= */}

        {message && (

          <div
            className="message"
            style={styles.message}
          >

            {message}

          </div>

        )}

      </div>

    </div>

  );
};


export default Settings;